// Cruise Command · Public Account & Data Deletion page.
//
// Google Play + Apple 5.1.1(v) compliance: an unauthenticated,
// direct-URL surface that documents (a) how a Cruise Command user
// can delete their account and associated personal data, and (b)
// how someone without an active login can request deletion. This
// page NEVER performs a destructive action itself — it explains
// the in-app self-service path and provides a compliant support
// request address for the exceptional case.
//
// No auth. No SSO. No secrets. No premium gating. No product data.
import React from "react";
import { BRAND_ICON, BRAND_META } from "@/lib/BrandAssets";

const CREAM = "#f5efe4";
const INK = "#0b132b";
const ACCENT = "#0f6a4d";
const SUPPORT_EMAIL = "support@mycruisecommand.com";

export default function AccountDeletionPage() {
  return (
    <div
      data-testid="cc-account-deletion-page"
      className="min-h-screen w-full bg-[#0b132b] text-slate-100"
      style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
    >
      <div
        className="max-w-3xl mx-auto px-7 md:px-12 py-16 md:py-20"
        style={{ background: CREAM, color: INK, minHeight: "100vh" }}
      >
        <img
          src={BRAND_ICON.primary}
          alt={BRAND_META.productName}
          width={72}
          height={72}
          className="select-none"
          draggable={false}
        />
        <div
          className="mt-6 text-[10px] font-black tracking-[0.28em] uppercase"
          style={{ color: ACCENT }}
        >
          Cruise Command · Legal
        </div>
        <h1
          className="mt-3 leading-[1.08]"
          style={{
            fontFamily: "'Fraunces', Georgia, serif",
            fontWeight: 500,
            fontSize: "clamp(2rem, 4vw, 3.2rem)",
          }}
        >
          Account &amp; Data Deletion
        </h1>
        <p className="mt-6 text-[15px] leading-relaxed" style={{ color: "#324058" }}>
          This page tells you how to delete your Cruise Command account and
          the personal data associated with it. It is provided by{" "}
          <strong>1001714184 ONTARIO INC.</strong> (operator of Cruise Command)
          in compliance with Google Play, Apple App Store, and applicable
          privacy legislation.
        </p>

        <Section title="1 · Delete your account from inside the app (recommended)">
          <p>
            If you can sign in to Cruise Command, the fastest way to delete
            your account is from the app itself. It takes about a minute:
          </p>
          <ol className="mt-3 ml-5 list-decimal space-y-1.5">
            <li>Open Cruise Command (mobile app or web).</li>
            <li>
              Sign in with the account you want to delete.
            </li>
            <li>
              Open <strong>Settings</strong>.
            </li>
            <li>
              Scroll to <strong>Delete my account</strong>.
            </li>
            <li>
              Type the confirmation phrase{" "}
              <code className="rounded bg-[#e9e2d1] px-1.5 py-0.5 text-[13px]">
                delete my account
              </code>{" "}
              and confirm.
            </li>
          </ol>
          <p className="mt-3">
            You will be signed out immediately on every device. Your account
            enters a 30-day pending-deletion window. Nothing is removed during
            that window: sign back in and choose Cancel deletion, or contact
            us, to restore everything. After 30 days the deletion is
            permanent.
          </p>
        </Section>

        <Section title="2 · Request deletion by e-mail (if you cannot sign in)">
          <p>
            If you have lost access to the account you want deleted, or you
            never had one but believe Cruise Command holds personal data
            about you, e-mail us from the address you
            registered with:
          </p>
          <p className="mt-3">
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=Account%20deletion%20request`}
              className="font-semibold underline"
              style={{ color: ACCENT }}
              data-testid="cc-account-deletion-support-email"
            >
              {SUPPORT_EMAIL}
            </a>
          </p>
          <p className="mt-3">
            Include the e-mail address associated with the account. We will
            verify your identity, action the request, and confirm by
            reply within 30 days. We will never delete an account on the
            basis of an unverified request.
          </p>
        </Section>

        <Section title="3 · What is deleted">
          <p>
            When your account is deleted, the following are removed from
            Cruise Command's active systems:
          </p>
          <ul className="mt-3 ml-5 list-disc space-y-1.5">
            <li>
              Your Cruise Command profile (name, e-mail, preferences, avatar).
            </li>
            <li>Your linked federated identities (Google, Microsoft, Apple).</li>
            <li>Your active sessions on every device.</li>
            <li>Your saved cruises, itineraries, dining reservations, add-ons, packing lists, and travel documents.</li>
            <li>Your traveller party records and personal details you entered.</li>
            <li>Your notes, memories, and the files you uploaded or created (documents, receipts, profile photo, published binders).</li>
            <li>Files another traveller uploaded to your voyage are moved to that traveller's own Documents. Documents you uploaded to another traveller's voyage before 23 October 2026 are kept for that voyage's owner only, without your account attached, and you can ask us to remove them; documents uploaded there from that date are deleted.</li>
          </ul>
        </Section>

        <Section title="4 · What may be retained, and why">
          <p>
            A small amount of information may be retained after your
            account is deleted, only where a specific legal or operational
            obligation requires it:
          </p>
          <ul className="mt-3 ml-5 list-disc space-y-1.5">
            <li>
              <strong>Membership and payment records</strong> — kept to
              meet tax, accounting and legal obligations.
            </li>
            <li>
              <strong>Audit and security logs</strong> that reference your
              account id, and records of how your uploaded documents were
              processed (which can include details extracted from them) —
              kept for security, accountability and legal purposes, not
              currently on a fixed schedule. A minimal deleted-account record
              holds no readable name or e-mail.
            </li>
            <li>
              <strong>Backup snapshots</strong> — managed by our hosting
              provider. Deleted data may remain in backups for a period we
              are confirming with that provider.
            </li>
          </ul>
          <p className="mt-3">
            None of the above is used for marketing or profiling after
            deletion.
          </p>
        </Section>

        <Section title="5 · Timing">
          <ul className="mt-1 ml-5 list-disc space-y-1.5">
            <li>Sign-out: immediate on every device.</li>
            <li>Grace period: 30 days (deletion can be cancelled).</li>
            <li>Permanent removal from active systems: at the first daily clean-up after the 30 days.</li>
            <li>Backups: managed by our hosting provider (period being confirmed).</li>
          </ul>
        </Section>

        <Section title="6 · Contact">
          <p>
            Questions about this page or your data:{" "}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="font-semibold underline"
              style={{ color: ACCENT }}
            >
              {SUPPORT_EMAIL}
            </a>
            .
          </p>
          <p className="mt-3">
            See also the{" "}
            <a
              href="https://mycruisecommand.com/legal/privacy.html"
              className="font-semibold underline"
              style={{ color: ACCENT }}
              data-testid="cc-account-deletion-privacy-link"
            >
              Cruise Command Privacy Policy
            </a>
            .
          </p>
        </Section>

        <div
          className="mt-16 pt-6 border-t text-[11px] leading-relaxed"
          style={{ borderColor: "rgba(11,19,43,0.15)", color: "#324058" }}
        >
          Cruise Command · 1001714184 ONTARIO INC. · Ontario, Canada · Last
          updated 23 October 2026.
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2
        className="text-[10px] font-black tracking-[0.28em] uppercase"
        style={{ color: ACCENT }}
      >
        {title.split(" · ")[0]}
      </h2>
      <div
        className="mt-2 mb-4"
        style={{
          fontFamily: "'Fraunces', Georgia, serif",
          fontWeight: 500,
          color: INK,
          fontSize: "clamp(1.25rem, 2.4vw, 1.6rem)",
        }}
      >
        {title.replace(/^\d+\s·\s/, "")}
      </div>
      <div className="text-[14.5px] leading-relaxed" style={{ color: "#324058" }}>
        {children}
      </div>
    </section>
  );
}
